import * as React from "react";
import { Form, Formik } from "formik";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { DrawerActions } from "components/DrawerActions/DrawerActions";
import { LoadingButton } from "components/LoadingButton/LoadingButton";
import { sanitizeFieldName } from "components/formikFields/formikUtils";
import { SnackbarApiError } from "components/SnackbarApiError/SnackbarApiError";
import {
  CustomFieldDefinition,
  CustomFieldValue,
  JobRequestFile,
  JobRequestFileCategory,
  JobRequestsService,
  JobRequestType,
  UtilsService
} from "gen/clients/llts";
import { useMutation, useQuery } from "react-query";
import { Grid } from "@mui/material";
import { TextInputField } from "components/formikFields/TextInputField/TextInputField";
import { CustomField } from "components/formikFields/CustomField/CustomField";
import { useTranslation } from "react-i18next";
import { SelectOption } from "@mui/base";
import { SelectOneAutocompleteField } from "components/formikFields/SelectOneAutocompleteField/SelectOneAutocompleteField";
import { SelectManyAutocompleteField } from "components/formikFields/SelectManyAutocompleteField/SelectManyAutocompleteField";
import { SELECT_OPTION_COMPARATOR } from "utils/stringUtils";
import { LoadingPage } from "components/LoadingPage/LoadingPage";
import { SelectOneField } from "components/formikFields/SelectOneField/SelectOneField";
import { JobRequestFileList } from "components/jobRequests/JobRequestFileList/JobRequestFileList";
import { createProjectName } from "utils/projectUtils";
import { CustomFieldUtils } from "components/formikFields/CustomField/CustomFieldUtils";
import { formatPriceWithCurrency } from "../../../../../utils/currencyUtils";
import { useLltsBranch } from "../../../../../hooks/useLltsBranch";

interface Props {
  jobRequestId: string;
  /** The type of the request (project or quote) to be created. */
  requestType: JobRequestType;
  requestName?: string;
  email: string;
  sourceLanguageId: string;
  targetLanguageIds: string[];
  projectCustomFieldDefinitions?: CustomFieldDefinition[];
  files: JobRequestFile[];
  translatedFilesCategoryOverride?: JobRequestFileCategory;
  hideReasonOptions?: boolean;
  /** Whether to request creating receivables based on AI LQR. Defaults to false. */
  createReceivablesBasedOnAiLqr?: boolean;
  hideNotes?: boolean;
  estimatedHumanEscalationPrice?: number;
  disableTargetLanguages?: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

enum FieldNames {
  projectName = "projectName",
  sourceLanguage = "sourceLanguage",
  targetLanguages = "targetLanguages",
  reason = "reason",
  otherReason = "otherReason",
  notes = "notes"
}

interface FormValues {
  [FieldNames.projectName]: string;
  [FieldNames.sourceLanguage]: SelectOption<string> | null;
  [FieldNames.targetLanguages]: SelectOption<string>[];
  [FieldNames.reason]: string;
  [FieldNames.otherReason]: string;
  [FieldNames.notes]: string;
}

enum RequestReason {
  FORMATTING = "Clean up formatting",
  TRANSLATION = "Need human translation",
  OTHER = "Other"
}

const HUMAN_TRANSLATION_FILE_LIST_MAX_HEIGHT = 240;

const RequestHumanTranslationPanelForm: React.FC<Props> = ({
  jobRequestId,
  requestType,
  requestName,
  email,
  sourceLanguageId,
  targetLanguageIds,
  projectCustomFieldDefinitions,
  files,
  translatedFilesCategoryOverride,
  hideReasonOptions,
  createReceivablesBasedOnAiLqr,
  hideNotes,
  estimatedHumanEscalationPrice,
  disableTargetLanguages,
  onClose,
  onSuccess
}) => {
  const { t } = useTranslation();
  const { mutateAsync, isLoading, error } = useMutation(JobRequestsService.submitToXtrf);
  const branch = useLltsBranch();

  const {
    data: languages,
    isLoading: areLanguagesLoading,
    error: languagesError
  } = useQuery("listXtrfLltsLanguages", {
    queryFn: () => UtilsService.listLltsLanguages({ xtrfIdExists: true })
  });
  const languageOptions = React.useMemo(
    () => languages?.map(l => ({ label: l.name, value: `${l.id}` })).sort(SELECT_OPTION_COMPARATOR) || [],
    [languages]
  );

  const getLanguageName = React.useCallback(
    (languageId: string | number | undefined) => {
      if (!languageId) {
        return null;
      }
      const option = languageOptions.find(o => o.value === `${languageId}`);
      return option?.label ? `${option.label}` : `${languageId}`;
    },
    [languageOptions]
  );

  const humanTranslationReasonOptions = React.useMemo(
    () => [
      {
        label: t("machineTranslation.requestHumanTranslation.reasons.CLEAN_UP_FORMATTING"),
        value: RequestReason.FORMATTING
      },
      {
        label: t("machineTranslation.requestHumanTranslation.reasons.NEED_HUMAN_TRANSLATION"),
        value: RequestReason.TRANSLATION
      },
      {
        label: t("machineTranslation.requestHumanTranslation.reasons.OTHER"),
        value: RequestReason.OTHER
      }
    ],
    [t]
  );

  const initialValues = React.useMemo(() => {
    const selectedSourceLanguageOption = languageOptions.find(o => o.value === sourceLanguageId);
    const selectedTargetLanguageOptions = languageOptions.filter(o => targetLanguageIds.includes(o.value));
    const standardFieldValues = {
      [FieldNames.projectName]: requestName || createProjectName(email),
      [FieldNames.sourceLanguage]: selectedSourceLanguageOption || null,
      [FieldNames.targetLanguages]: selectedTargetLanguageOptions || [],
      [FieldNames.notes]: "",
      [FieldNames.otherReason]: "",
      [FieldNames.reason]: hideReasonOptions ? RequestReason.OTHER : ""
    };
    const customFieldValues: Record<string, string | string[]> = {};
    projectCustomFieldDefinitions?.forEach((customFieldDefinition: CustomFieldDefinition) => {
      customFieldValues[sanitizeFieldName(customFieldDefinition.name)] = customFieldDefinition.defaultValue || "";
    });
    return {
      ...standardFieldValues,
      ...customFieldValues
    };
  }, [
    languageOptions,
    requestName,
    email,
    hideReasonOptions,
    projectCustomFieldDefinitions,
    sourceLanguageId,
    targetLanguageIds
  ]);

  const onSubmit = React.useCallback(
    async (values: FormValues) => {
      const customFieldValues: CustomFieldValue[] | undefined = projectCustomFieldDefinitions?.map(fieldDefinition => ({
        fieldName: fieldDefinition.name,
        mapping: fieldDefinition.mapping,
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        value: `${values[sanitizeFieldName(fieldDefinition.name)]}`
      }));

      let reasonText = values[FieldNames.reason];
      if (values[FieldNames.reason] === RequestReason.OTHER && values[FieldNames.otherReason]) {
        reasonText = values[FieldNames.otherReason];
      }

      const notes = `Source: Translation Portal Self Service MT File app escalation to human expert\nReason for request: ${reasonText}\n\n${values[FieldNames.notes]}`;
      const selectedTargetLanguageIds = values[FieldNames.targetLanguages].map(o => o.value);
      let filesToSubmit: JobRequestFile[] = [...files];
      // Assign translated files to a category specified by translatedFilesCategoryOverride, if it is set
      if (translatedFilesCategoryOverride) {
        filesToSubmit = filesToSubmit.map(f =>
          f.category === JobRequestFileCategory.TRANSLATED_DOCUMENT
            ? { ...f, category: translatedFilesCategoryOverride }
            : f
        );
      }
      // Per LLTS-1816, assign to translated files category OTHER if selected reason is translation
      else if (values[FieldNames.reason] === RequestReason.TRANSLATION) {
        filesToSubmit = filesToSubmit.map(f =>
          f.category === JobRequestFileCategory.TRANSLATED_DOCUMENT
            ? { ...f, category: JobRequestFileCategory.OTHER }
            : f
        );
      }
      await mutateAsync(
        {
          jobRequestId,
          requestBody: {
            projectName: values[FieldNames.projectName] || "",
            requestType,
            sourceLanguageId: values[FieldNames.sourceLanguage]?.value,
            targetLanguageIds: selectedTargetLanguageIds,
            notes,
            customFields: customFieldValues,
            files: filesToSubmit,
            createReceivablesBasedOnAiLqr
          }
        },
        { onSuccess }
      );
    },
    [
      createReceivablesBasedOnAiLqr,
      files,
      jobRequestId,
      mutateAsync,
      onSuccess,
      projectCustomFieldDefinitions,
      requestType,
      translatedFilesCategoryOverride
    ]
  );

  const submitButtonText = React.useMemo(() => {
    if (requestType === JobRequestType.TRANSLATION_REQUEST) {
      if (estimatedHumanEscalationPrice) {
        return (
          t("machineTranslation.requestHumanTranslation.requestHumanExpert") +
          " - " +
          formatPriceWithCurrency(estimatedHumanEscalationPrice, branch?.currency || "USD")
        );
      } else {
        return t("machineTranslation.requestHumanTranslation.requestHumanExpert");
      }
    }
    return t("machineTranslation.requestHumanTranslation.submitQuoteRequest");
  }, [branch?.currency, estimatedHumanEscalationPrice, requestType, t]);

  const displayFiles = React.useMemo(
    () =>
      files
        .filter(f => f.category !== JobRequestFileCategory.SOURCE_DOCUMENT)
        .map(f => ({
          ...f,
          languageId: f.languageId ? `${f.languageId}` : sourceLanguageId
        })),
    [files, sourceLanguageId]
  );

  if (areLanguagesLoading) {
    return <LoadingPage />;
  }

  return (
    <Formik initialValues={initialValues} onSubmit={onSubmit}>
      {({ values }) => (
        <Form noValidate={true} autoComplete="off">
          {error && <SnackbarApiError error={error} />}
          <Typography variant="body1" mb={3}>
            {t("machineTranslation.requestHumanTranslation.provideAdditionalInformation")}
          </Typography>
          <Grid container={true} spacing={2}>
            {/* Project name */}
            <Grid item xs={12}>
              <TextInputField
                name={FieldNames.projectName}
                label={t("machineTranslation.requestHumanTranslation.projectName.label")}
                helperText={t("machineTranslation.requestHumanTranslation.projectName.helperText")}
                required={true}
                preventCharacters={[";"]}
                preventCharactersMessage={t("common.validation.semicolonNotAllowed")}
                maxLength={140}
                maxLengthMessage={t("common.validation.projectNameMaxLength")}
              />
            </Grid>
            <Grid item xs={12}>
              <SelectOneAutocompleteField
                name={FieldNames.sourceLanguage}
                label={t("createProject.createProjectForm.sourceLanguage.label")}
                helperText={t("createProject.createProjectForm.sourceLanguage.helperText")}
                options={languageOptions}
                isLoading={areLanguagesLoading}
                isError={!!languagesError}
                required={true}
                disabled={true}
              />
            </Grid>
            <Grid item xs={12}>
              <SelectManyAutocompleteField
                name={FieldNames.targetLanguages}
                label={t("createProject.createProjectForm.targetLanguage.label")}
                helperText={t("createProject.createProjectForm.targetLanguage.helperText")}
                options={languageOptions}
                isLoading={areLanguagesLoading}
                isError={!!languagesError}
                required={true}
                disabled={disableTargetLanguages}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                {t("machineTranslation.requestHumanTranslation.files")}
              </Typography>
              <Box
                sx={{
                  maxHeight: HUMAN_TRANSLATION_FILE_LIST_MAX_HEIGHT,
                  overflowY: "auto",
                  border: theme => `1px solid ${theme.palette.divider}`,
                  borderRadius: 1,
                  px: 1.5,
                  pb: 1.5
                }}
              >
                {displayFiles.length > 0 ? (
                  <JobRequestFileList
                    jobRequestId={jobRequestId}
                    files={displayFiles}
                    groupByLanguage={true}
                    getLanguageName={getLanguageName}
                  />
                ) : (
                  <Typography variant="body2">{t("machineTranslation.fileTranslation.requests.noFiles")}</Typography>
                )}
              </Box>
            </Grid>
            {/* Project custom fields */}
            {projectCustomFieldDefinitions &&
              projectCustomFieldDefinitions.map(customFieldDefinition => (
                <React.Fragment key={customFieldDefinition.name}>
                  {CustomFieldUtils.isCustomFieldVisible(customFieldDefinition, values) && (
                    <Grid item xs={12}>
                      <CustomField customFieldDefinition={customFieldDefinition} />
                    </Grid>
                  )}
                </React.Fragment>
              ))}
            {!hideReasonOptions && (
              <Grid item={true} xs={12}>
                <SelectOneField
                  name={FieldNames.reason}
                  label={t("machineTranslation.requestHumanTranslation.reason")}
                  required={true}
                  options={humanTranslationReasonOptions}
                />
              </Grid>
            )}
            {/* Show text field for Other reason */}
            {values[FieldNames.reason] === RequestReason.OTHER && (
              <Grid item xs={12}>
                <TextInputField
                  name={FieldNames.otherReason}
                  label={t("machineTranslation.requestHumanTranslation.otherReason")}
                  required={true}
                  multiline={true}
                  rows={3}
                  variant="outlined"
                />
              </Grid>
            )}
            {/* Notes */}
            {!hideNotes && (
              <Grid item xs={12}>
                <TextInputField
                  name={FieldNames.notes}
                  label={t("machineTranslation.requestHumanTranslation.notes")}
                  multiline={true}
                  rows={values[FieldNames.reason] === RequestReason.OTHER ? 3 : 5}
                  variant="outlined"
                />
              </Grid>
            )}
          </Grid>
          <DrawerActions>
            <LoadingButton isLoading={isLoading}>{submitButtonText}</LoadingButton>
            <Button onClick={onClose} sx={{ ml: 1 }}>
              {t("machineTranslation.requestHumanTranslation.close")}
            </Button>
          </DrawerActions>
        </Form>
      )}
    </Formik>
  );
};

export { RequestHumanTranslationPanelForm };
